import Phaser from 'phaser';
import { TEXTURE_KEYS } from '../../assets/assetKeys';
import { VISUAL_THEME } from '../../assets/visualTheme';
import { GAME_HEIGHT, GAME_WIDTH } from '../../data/gameConstants';
import { ObstacleRect } from '../../systems/ZoneCoreSystems';
import { UIStyle } from '../../ui/UIStyle';

export const ZONE5_POSITIONS = {
  SPAWN: { x: 140, y: 550 },
  VULNERABLE: { x: 485, y: 520 },
  LANSIA: { x: 428, y: 530 },
  IBU_HAMIL: { x: 542, y: 530 },
  VULNERABLE_HINT: { x: 485, y: 438 },
  VULNERABLE_QUEST_HINT: { x: 485, y: 398 },
  SAFE_ZONE: { x: 860, y: 480 },
  IBUNDO: { x: 1120, y: 450 },
  IBUNDO_LABEL: { x: 1120, y: 388 },
  IBUNDO_HINT: { x: 1120, y: 318 },
  IBUNDO_BLOCK: { x: 1040, y: 450, width: 50, height: 180 },
} as const;

export class Zone5Map {
  private scene: Phaser.Scene;
  private ibundoBlocker?: Phaser.GameObjects.Rectangle;
  private ibundoLabel?: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  build(): ObstacleRect[] {
    this.drawBackground();
    this.drawPath();
    this.drawSafeZone();
    this.drawDecorations();
    return this.buildObstacles();
  }

  openIbundoPath(): void {
    this.ibundoBlocker?.destroy();
    this.ibundoBlocker = undefined;
    this.ibundoLabel?.setColor('#52b788');
    this.ibundoLabel?.setText('Ibundo Putri Rimas\n(Terbuka)');
  }

  private drawBackground(): void {
    this.scene.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, TEXTURE_KEYS.BG_ZONE5)
      .setDisplaySize(GAME_WIDTH, GAME_HEIGHT)
      .setDepth(0);

    this.scene.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT,
      VISUAL_THEME.zoneWarmth.zone5, 0.08,
    ).setDepth(0.5);

    const grass = this.scene.add.graphics().setDepth(1);
    grass.fillStyle(0x52b788, 0.25);
    grass.fillEllipse(GAME_WIDTH / 2, 560, 1100, 180);
    grass.fillStyle(0xf2c14e, 0.08);
    grass.fillCircle(1060, 92, 44);
  }

  private drawPath(): void {
    const path = this.scene.add.graphics().setDepth(2);
    path.fillStyle(0xa1887f, VISUAL_THEME.pathAlpha);
    const pts = [
      { x: 120, y: 560 },
      { x: 350, y: 530 },
      { x: 550, y: 510 },
      { x: 750, y: 490 },
      { x: 950, y: 480 },
      { x: 1150, y: 460 },
    ];
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i];
      const b = pts[i + 1];
      path.fillEllipse((a.x + b.x) / 2, (a.y + b.y) / 2, 110, 38);
    }
  }

  private drawSafeZone(): void {
    const safe = ZONE5_POSITIONS.SAFE_ZONE;
    this.scene.add.image(safe.x, safe.y - 20, TEXTURE_KEYS.SAFE_GATHERING)
      .setScale(0.75).setDepth(3).setAlpha(0.85);

    this.scene.add.circle(safe.x, safe.y, 90, 0x52b788, 0.15).setDepth(2);
    this.scene.add.circle(safe.x, safe.y, 90).setStrokeStyle(3, 0x52b788, 0.6).setDepth(2);
    this.scene.add.circle(safe.x, safe.y, 120).setStrokeStyle(2, 0xf2c14e, 0.25).setDepth(2);

    this.scene.add.text(safe.x, safe.y - 75, 'Titik Kumpul Aman', {
      ...UIStyle.mapLabel(16, '#7dffb8'),
    }).setOrigin(0.5).setDepth(5);
  }

  private drawDecorations(): void {
    const wargaPos = [
      { x: 280, y: 510 },
      { x: 620, y: 500 },
      { x: 720, y: 490 },
    ];
    wargaPos.forEach((p) => {
      this.scene.add.image(p.x, p.y, TEXTURE_KEYS.WARGA).setScale(0.9).setDepth(4).setAlpha(0.7);
    });
    this.scene.add.image(320, 566, TEXTURE_KEYS.TREE_FALLEN).setScale(0.62).setDepth(2.8).setAngle(6).setAlpha(0.7);

    this.scene.add.image(ZONE5_POSITIONS.IBUNDO.x, ZONE5_POSITIONS.IBUNDO.y,
      TEXTURE_KEYS.IBUNDO,
    ).setScale(1.1).setDepth(4);

    this.ibundoLabel = this.scene.add.text(
      ZONE5_POSITIONS.IBUNDO_LABEL.x,
      ZONE5_POSITIONS.IBUNDO_LABEL.y,
      'Ibundo Putri Rimas\n(Terkunci)',
      {
        ...UIStyle.mapLabel(14, '#ff6b6b'),
        align: 'center',
        lineSpacing: 4,
      },
    ).setOrigin(0.5, 1).setDepth(5);

    this.scene.add.text(
      ZONE5_POSITIONS.VULNERABLE_HINT.x,
      ZONE5_POSITIONS.VULNERABLE_HINT.y,
      'Dahulukan kelompok rentan',
      UIStyle.mapLabel(14, '#ffd966'),
    ).setOrigin(0.5, 1).setDepth(5);

    this.scene.add.text(
      ZONE5_POSITIONS.VULNERABLE_HINT.x,
      ZONE5_POSITIONS.VULNERABLE_HINT.y + 8,
      'Lansia · Ibu Hamil',
      UIStyle.mapLabel(13, '#eef2f8'),
    ).setOrigin(0.5, 0).setDepth(5);
    this.scene.add.image(960, 522, TEXTURE_KEYS.KERIS_1).setScale(0.7).setDepth(4).setAngle(8).setAlpha(0.95);
  }

  private buildObstacles(): ObstacleRect[] {
    const obs: ObstacleRect[] = [
      { x: GAME_WIDTH / 2, y: 150, width: GAME_WIDTH, height: 240 },
      { x: GAME_WIDTH / 2, y: GAME_HEIGHT - 15, width: GAME_WIDTH, height: 30 },
      { x: 25, y: GAME_HEIGHT / 2, width: 50, height: GAME_HEIGHT },
      { x: GAME_WIDTH - 15, y: GAME_HEIGHT / 2, width: 30, height: GAME_HEIGHT },
    ];

    const block = ZONE5_POSITIONS.IBUNDO_BLOCK;
    this.ibundoBlocker = this.scene.add.rectangle(block.x, block.y, block.width, block.height, 0xc1121f, 0.3);
    this.ibundoBlocker.setDepth(6);
    obs.push({ x: block.x, y: block.y, width: block.width, height: block.height });

    return obs;
  }
}
